import { Injectable } from "@angular/core";
import * as moment from "moment";
import { Period } from "../models/period.model";
import { LanguageService } from "./language.service";

@Injectable({
    providedIn: "root"
})
export class PeriodService {
    constructor(private languageService: LanguageService) {}

    getPeriod(fromDate: string, toDate: string, dateRangeIndex = 1): Period[] {
        const from = new Date(moment(fromDate, "DD.MM.YYYY").format("MM.DD.YYYY"));
        const to = new Date(moment(toDate, "DD.MM.YYYY").format("MM.DD.YYYY"));

        switch (dateRangeIndex) {
            case 0: return this.getPeriodFromDay(from);
            case 1: return this.getPeriodFromWeek(from);
            case 2: return this.getPeriodFromMonth(from, to);
            case 3: return this.getPeriodFromYear(from);
        }
    }

    getMomentPeriod(fromDate: moment.Moment, toDate: moment.Moment, dateRangeIndex = 1, useSimpleNames = false): Period[] {
      switch (dateRangeIndex) {
          case 0: return this.getPeriodFromDay(fromDate.toDate());
          case 1: return this.getPeriodFromWeek(fromDate.toDate(), useSimpleNames);
          case 2: return this.getPeriodFromMonth(fromDate.toDate(), toDate.toDate());
          case 3: return this.getPeriodFromYear(fromDate.toDate(), useSimpleNames);
      }
    }

    getActualPeriod(periods: Period[], date: Date): Period {
      for (let i = 0; i < periods.length; i++) {
        const period = periods[i];
        if (date >= period.fromDate && date <= period.toDate) { return period; }
      }

      return undefined;
    }

    private getPeriodFromDay(date: Date): Period[] {
        return [new Period(date, date, moment(date).format("dddd DD MMM"))];
    }

    private getPeriodFromWeek(date: Date, useSimpleNames = false): Period[] {
        return !useSimpleNames ? [
            new Period(date, date, moment(date).format("ddd DD MMM")),
            new Period(moment(date).add(1, "days").toDate(), moment(date).add(1, "days").toDate(), moment(date).add(1, "days").format("ddd DD MMM")),
            new Period(moment(date).add(2, "days").toDate(), moment(date).add(2, "days").toDate(), moment(date).add(2, "days").format("ddd DD MMM")),
            new Period(moment(date).add(3, "days").toDate(), moment(date).add(3, "days").toDate(), moment(date).add(3, "days").format("ddd DD MMM")),
            new Period(moment(date).add(4, "days").toDate(), moment(date).add(4, "days").toDate(), moment(date).add(4, "days").format("ddd DD MMM")),
            new Period(moment(date).add(5, "days").toDate(), moment(date).add(5, "days").toDate(), moment(date).add(5, "days").format("ddd DD MMM")),
            new Period(moment(date).add(6, "days").toDate(), moment(date).add(6, "days").toDate(), moment(date).add(6, "days").format("ddd DD MMM"))
        ] : [
            new Period(date, date, this.languageService.LNG("Label.Monday")),
            new Period(moment(date).add(1, "days").toDate(), moment(date).add(1, "days").toDate(), this.languageService.LNG("Label.Tuesday")),
            new Period(moment(date).add(2, "days").toDate(), moment(date).add(2, "days").toDate(), this.languageService.LNG("Label.Wednesday")),
            new Period(moment(date).add(3, "days").toDate(), moment(date).add(3, "days").toDate(), this.languageService.LNG("Label.Thursday")),
            new Period(moment(date).add(4, "days").toDate(), moment(date).add(4, "days").toDate(), this.languageService.LNG("Label.Friday")),
            new Period(moment(date).add(5, "days").toDate(), moment(date).add(5, "days").toDate(), this.languageService.LNG("Label.Saturday")),
            new Period(moment(date).add(6, "days").toDate(), moment(date).add(6, "days").toDate(), this.languageService.LNG("Label.Sunday"))
        ];
    }

    private getPeriodFromMonth(fromDate: Date, toDate: Date): Period[] {
        const label = this.languageService.LNG("Label.Week");
        const period = [];
        let date = fromDate;

        while (date < toDate) {
            period.push(new Period(moment(date).startOf("isoWeek").toDate(), moment(date).endOf("isoWeek").toDate(), `${label} ${moment(date).format("WW")}`));
            date = moment(date).add(1, "weeks").toDate();
        }

        return period;
    }

    private getPeriodFromYear(date: Date, useSimpleNames = false): Period[] {
        return ! useSimpleNames ? [
            new Period(date, moment(date).add(1, "months").subtract(1, "days").toDate(), moment(date).format("MMM YYYY")),
            new Period(moment(date).add(1, "months").toDate(), moment(date).add(2, "months").subtract(1, "days").toDate(), moment(date).add(1, "months").format("MMM YYYY")),
            new Period(moment(date).add(2, "months").toDate(), moment(date).add(3, "months").subtract(1, "days").toDate(), moment(date).add(2, "months").format("MMM YYYY")),
            new Period(moment(date).add(3, "months").toDate(), moment(date).add(4, "months").subtract(1, "days").toDate(), moment(date).add(3, "months").format("MMM YYYY")),
            new Period(moment(date).add(4, "months").toDate(), moment(date).add(5, "months").subtract(1, "days").toDate(), moment(date).add(4, "months").format("MMM YYYY")),
            new Period(moment(date).add(5, "months").toDate(), moment(date).add(6, "months").subtract(1, "days").toDate(), moment(date).add(5, "months").format("MMM YYYY")),
            new Period(moment(date).add(6, "months").toDate(), moment(date).add(7, "months").subtract(1, "days").toDate(), moment(date).add(6, "months").format("MMM YYYY")),
            new Period(moment(date).add(7, "months").toDate(), moment(date).add(8, "months").subtract(1, "days").toDate(), moment(date).add(7, "months").format("MMM YYYY")),
            new Period(moment(date).add(8, "months").toDate(), moment(date).add(9, "months").subtract(1, "days").toDate(), moment(date).add(8, "months").format("MMM YYYY")),
            new Period(moment(date).add(9, "months").toDate(), moment(date).add(10, "months").subtract(1, "days").toDate(), moment(date).add(9, "months").format("MMM YYYY")),
            new Period(moment(date).add(10, "months").toDate(), moment(date).add(11, "months").subtract(1, "days").toDate(), moment(date).add(10, "months").format("MMM YYYY")),
            new Period(moment(date).add(11, "months").toDate(), moment(date).add(12, "months").subtract(1, "days").toDate(), moment(date).add(11, "months").format("MMM YYYY"))
        ] : [
            new Period(date, moment(date).add(1, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.January").substring(0, 3)),
            new Period(moment(date).add(1, "months").toDate(), moment(date).add(2, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.February").substring(0, 3)),
            new Period(moment(date).add(2, "months").toDate(), moment(date).add(3, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.March").substring(0, 3)),
            new Period(moment(date).add(3, "months").toDate(), moment(date).add(4, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.April").substring(0, 3)),
            new Period(moment(date).add(4, "months").toDate(), moment(date).add(5, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.May").substring(0, 3)),
            new Period(moment(date).add(5, "months").toDate(), moment(date).add(6, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.June").substring(0, 3)),
            new Period(moment(date).add(6, "months").toDate(), moment(date).add(7, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.July").substring(0, 3)),
            new Period(moment(date).add(7, "months").toDate(), moment(date).add(8, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.August").substring(0, 3)),
            new Period(moment(date).add(8, "months").toDate(), moment(date).add(9, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.September").substring(0, 3)),
            new Period(moment(date).add(9, "months").toDate(), moment(date).add(10, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.October").substring(0, 3)),
            new Period(moment(date).add(10, "months").toDate(), moment(date).add(11, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.November").substring(0, 3)),
            new Period(moment(date).add(11, "months").toDate(), moment(date).add(12, "months").subtract(1, "days").toDate(), this.languageService.LNG("Label.December").substring(0, 3))
        ];
    }
}
